import '../../sp-image-editor/sp-image-editor';
import './sp-image-input-entry/sp-image-input-entry';
import '../../containers/sp-field-container';
import '../../../components/sp-drop-zone/sp-drop-zone';

import {SpBaseInput} from '../sp-base-input';
import {customElement, property, query, state} from 'lit/decorators.js';
import {SpDefaultDialog} from '../../sp-dialog/sp-default-dialog';
import {html, nothing, PropertyValues} from 'lit';
import {translate} from '@stefanholzapfel/lit-translate';
import {when} from 'lit/directives/when.js';
import {EditorSavedEvent, SpImageEditor, SpStickerConfig} from '../../sp-image-editor/sp-image-editor';
import {classMap} from 'lit/directives/class-map.js';
import {styles} from './sp-image-input.styles';
import {bubbleButton} from '../../../styles/buttons/bubble-button.styles';
import {SpValidationService} from "../../../services/sp-validation.service";
import {pulsateAnimation} from "../../../styles/animations/pulsate.animation";
import {scaleAnimation} from "../../../styles/animations/scale.animation";
import {SpDropZoneAnimation} from "../../sp-drop-zone/sp-drop-zone";
import {styleMap} from "lit-html/directives/style-map.js";
import {v4 as uuidv4} from 'uuid';
import {SpExif, SpFile, SpFileSource, SpRemoteFile} from "../interfaces";
import {SpImageService} from "../../../services/sp-image.service";
import exifr from 'exifr/dist/full.esm.mjs'
import {unsafeHTML} from "lit/directives/unsafe-html.js";
import {SpPhotoswipeController} from "../../../controllers/sp-photoswipe.controller";
import {getIcon} from "../../../styles/icons";

@customElement('sp-image-input')
export class SpImageInput extends SpBaseInput<(SpFile | SpRemoteFile)[]> {
    static styles = [
        styles,
        bubbleButton
    ];

    @property() type: 'default' | 'bubble' = 'default';
    @property({type: Array})
    value = [];
    @property() rootImage: SpRemoteFile;
    @property() rootImageLabel: string;
    @property() placeholder: string = getIcon('image-target', { color: 'grey-light', size: 'x256' });
    // If images are editable in editor. False will only display image on click
    @property({type: Boolean}) editable = true;
    @property({type: Object}) editorStickers: SpStickerConfig[];
    @property({type: Boolean, reflect: true}) disabled = false;
    // A list of allowed mime types
    @property({type: Array})
    allowedFileTypes: string[] = ['image/*'];
    // A list of allowed sources of images
    @property({type: Array})
    allowedFileSources: SpFileSource[] = [ 'recording', 'localFile' ];
    // Max. number of files allowed
    @property({type: Number})
    maxlength = 1;
    // Max. size of uploaded files in bytes
    @property({type: Number})
    maxsize = 6291456;
    @property({type: Boolean})
    viewMode = false;
    // Number of images per gallery row. ATTENTION: for browser not supporting the aspect-ratio css property the ratio value is only correct for 5 images per row
    @property({type: Number})
    entriesPerRow = 5;
    @property({type: Boolean})
    readExif = true;
    // Provide callback with your own implementation of taking a picture from camera. Will suppress default behaviour.
    @property()
    takePictureCallback: () => Promise<Blob>
    // Suppress attachment of a searchParam on opening gallery or dialog
    @property({type: Boolean})
    suppressUrlState;

    @state() latestDraggingMovePosition;
    @state() cameraDevice;

    // Necessary to filter drag events from other image inputs
    imageInputId = uuidv4();
    photoswipeController: SpPhotoswipeController;

    @query('#editor-dialog')
    editorDialog: SpDefaultDialog;
    @query('#error-dialog')
    errorDialog: SpDefaultDialog;

    dropZoneAnimations: SpDropZoneAnimation<any>[] = [
        {
            animation: pulsateAnimation,
            animationOptions: {
                minScale: 1,
                maxScale: 1.03,
                steadyPulse: true,
                duration: 1
            },
            animationTargetPart: 'container',
            animationTargetStates: [ 'dragging' ]
        },
        {
            animation: scaleAnimation,
            animationOptions: {
                start: 1,
                duration: 0.5
            },
            animationTargetPart: 'icon',
            animationTargetStates: [ 'dragOver' ]
        }
    ];
    deleteDropZoneAnimations: SpDropZoneAnimation<any>[] = [
        {
            animation: scaleAnimation,
            animationOptions: {
                start: 1,
                duration: 0.5
            },
            animationTargetPart: 'icon',
            animationTargetStates: [ 'dragOver' ]
        }
    ]

    constructor() {
        super();
        this.checkCamPresence().then(present => this.cameraDevice = present);
    }

    protected firstUpdated(_changedProperties: PropertyValues) {
        super.firstUpdated(_changedProperties);
        this.photoswipeController = new SpPhotoswipeController(this, {galleryId: this.imageInputId, suppressUrlState: this.suppressUrlState});
    }

    protected willUpdate(_changedProperties: PropertyValues) {
        super.willUpdate(_changedProperties);
        if (!this.value) this.value = [];
        if (_changedProperties.has('maxlength') && (isNaN(this.maxlength) || this.maxlength <= 0)) {
            this.maxlength = 1;
        }
    }

    async addedFromDropZone(e: DragEvent) {
        const files = [];
        for (const item of Array.from(e.dataTransfer.items)) {
            if (item.kind === 'file') {
                files.push(item.getAsFile());
            }
        }
        // Need to do this in a separate loop since await leaves the context where dataTransferItems are accessible
        for (const [ index, file ] of files.entries()) {
            if (this.maxlength > 1 || index === 0)
            await this.addImage(file, 'localFile');
        }
    }

    addedFromFilepicker(change: InputEvent) {
        const image = (change.target as any).files[0];
        (this.shadowRoot.getElementById('filePicker') as HTMLInputElement).value = '';
        this.addImage(image, 'localFile');
    }

    async addedFromCamera() {
        let image;
        if (this.takePictureCallback) {
            image = await this.takePictureCallback();
        } else {
            console.error("Not implemented!");
            // TODO: implement. using capacitor or https://developer.mozilla.org/en-US/docs/Web/API/Media_Capture_and_Streams_API/Taking_still_photos ?
            // TODO: set SpFile source to 'recording'
        }
        if (image instanceof Blob)
            this.addImage(image, 'recording');
    }

    async addImage(image, source: SpFileSource) {
        if (this.validateImage(image)) {
            let exif = this.readExif && !(source === 'recording') ? await this.getExif(image) : undefined;
            const position = this.maxlength === 1 && this.value.length === 1 ? 0 : this.value.length;
            if (this.editable) {
                await this.openEditor(image, position, { exif, source });
            } else {
                this.save(image, position, { exif, source });
            }
        }
    }

    validateImage(image: Blob): boolean {
        let error;
        if (this.maxlength > 1 && this.value.length === this.maxlength)
            error = html`${error}${ translate('UI_COMPONENT_ERROR_MAXFILES', { maxlength: this.maxlength.toString() }) }<br>`
        if (!image.type || !SpValidationService.validateMimeTypes(image.type, this.allowedFileTypes))
            error = html`${ translate('UI_COMPONENT_IMAGE_INPUT_ERROR_MIME_TYPE') }<br>`;
        if (!SpValidationService.validateFileSize(image, this.maxsize))
            error = html`${error}${ translate('UI_COMPONENT_IMAGE_INPUT_ERROR_SIZE', {size: (Math.round(this.maxsize / 1048576 * 100) / 100).toString() }) }<br>`;
        if (!!error) {
            this.errorDialog.template = error;
            this.errorDialog.show();
            return false;
        }
        return true;
    }

    async openImage(image: Blob | string, position: number, canvas?: object) {
        if (image) {
            if (this.disabled || !this.editable || this.viewMode) {
                await this.photoswipeController.updateData(this.value.map(img=> {
                    return {
                        fullSize: SpImageService.getUrlOrBlobFromImage(img),
                        // TODO: provide thumbs when working
                        thumb: SpImageService.getUrlOrBlobFromImage(img)
                    }
                }));
                this.photoswipeController.open(position);
            } else {
                this.openEditor(image, position, { canvas });
            }
        }
    }

    async openRootImage() {
        await this.photoswipeController.updateData([ {
            fullSize: SpImageService.getUrlOrBlobFromImage(this.rootImage),
            // TODO: provide thumbs when working
            thumb: SpImageService.getUrlOrBlobFromImage(this.rootImage)
        } ]);
        this.photoswipeController.open();
    }

    async openEditor(image, position, options?: { canvas?: object, exif?: SpExif, source?: SpFileSource }): Promise<'closed'> {
        return new Promise(resolve => {
            this.editorDialog.template =
                html`
                <sp-image-editor
                        @save-editor=${ event => { 
                            this.saveFromEditor(event, position, { exif: options?.exif, source: options?.source })
                        } }
                        .delayUntil=${ this.editorDialog.show() }
                        .image=${ options.canvas ?? image }
                        .stickers="${ this.editorStickers }">
                </sp-image-editor>`;
            const afterHideCallback = () => {
                resolve('closed');
                this.editorDialog.removeEventListener('after-hide', afterHideCallback);
            }
            this.editorDialog.addEventListener('after-hide', afterHideCallback);
        });
    }

    saveFromEditor(event: EditorSavedEvent, position, options?: { exif?: SpExif, source?: SpFileSource }) {
        this.save(event.detail.image, position, { canvas: event.detail.canvasData, exif: options?.exif, source: options?.source });
    }

    save(blob, position, options?: { canvas?: object, exif?: SpExif, source?: SpFileSource } ) {
        const file = {
            blob,
            ...options?.canvas && { canvas: options?.canvas },
            spec: {
                id: uuidv4(),
                ...options?.exif && { exif: options.exif },
                ...options?.source && { source: options.source }
            }
        };
        if (this.value[position]) {
            this.value[position] = file;
        } else {
            this.value.push(file);
        }
        delete (this.value[position] as SpRemoteFile).path;
        this.requestUpdate();
        this.emit(
            'change',
            {
                detail: { value: this.value }
            }
        );
    }

    async checkCamPresence(): Promise<undefined | MediaDeviceInfo> {
        const devices = await navigator.mediaDevices.enumerateDevices();
        for (const device of devices) {
            if (device.kind === 'videoinput') return device;
        }
        return undefined;
    }

    render() {
        const buttonCount = this.buttonCount();
        return html`
            <sp-default-dialog
                    id="editor-dialog"
                    ?suppressUrlState=${this.suppressUrlState}
                    .buttons=${[
                        {
                            text: translate('UI_COMPONENT_OK'),
                            textPosition: 'center',
                            clickCallback: async (elems) => {
                                await (elems[0] as SpImageEditor).save();
                                this.editorDialog.hide();
                            }
                        },
                        {
                            text: translate('UI_COMPONENT_CANCEL'),
                            textPosition: 'center',
                            backgroundColor: `rgb(var(--sp-greymedium))`,
                            clickCallback: async (elems) => {
                                this.editorDialog.hide();
                            }
                        }
                    ]}
                    .fullHeight=${true}
                    .defaultClearContentOnHide=${false}>
            </sp-default-dialog>
            <sp-default-dialog
                    id="error-dialog"
                    ?suppressUrlState=${this.suppressUrlState}
                    .headerText=${translate('UI_COMPONENT_ERROR')}
                    .buttons=${[
                        {
                            text: translate('UI_COMPONENT_OK'),
                            textPosition: 'center',
                            clickCallback: async (elems) => {
                                this.errorDialog.hide();
                            }
                        },

                    ]}>
            </sp-default-dialog>
            <div class="dropzone-container">
                ${ when(this.maxlength === 1 || this.value.length < this.maxlength, () => html`
                    <sp-drop-zone
                        @drop=${ this.addedFromDropZone }
                        icon=${getIcon('plus-circle')}
                        .mandatoryDataTypes=${ this.allowedFileTypes }
                        .animations=${this.dropZoneAnimations}>
                    </sp-drop-zone>
                `) }
                <div
                    class=${ classMap({
                    container: true,
                    bubble: this.type === 'bubble',
                    'view-mode': this.viewMode})}>
                    <sp-image-input-entry
                        id="image-input-entry-0"
                        draggable=${ this.value.length > 0 && !this.disabled && !this.viewMode }
                        @dragstart=${ e => { this.imageDragStart(e, 0) } }
                        @image-dragenter=${ e => this.imageDragEnter(e, 0) }
                        @open=${ e => { this.value.length > 0 ? this.openImage(SpImageService.getUrlOrBlobFromImage(this.value[0]), 0,  (this.value[0] as SpFile).canvas) : this.openFilePicker() } }
                        .image=${ SpImageService.getUrlOrBlobFromImage(this.value[0]) }
                        .canvas=${ (this.value[0] as SpFile)?.canvas }
                        .placeholder=${ this.placeholder }
                        .type=${ this.type }
                        .uuid=${this.imageInputId}>
                    </sp-image-input-entry>
                    <div class=${ classMap({
                        buttons: true,
                        bubble: this.type === 'bubble'
                    })}>
                        <sp-drop-zone
                            class="delete-zone"
                            .animations=${ this.deleteDropZoneAnimations }
                            .mandatoryDataTypes=${ [ `sp-image-input-entry-move-${this.imageInputId}` ] }
                            icon=${ getIcon('trash-circle', { color: 'grey-dark-filled', size: 'x128' }) }
                            @drop=${ e => { this.deleteEntry(this.latestDraggingMovePosition) }}>
                        </sp-drop-zone>
                        ${
                            when((this.cameraDevice || this.takePictureCallback) && this.allowedFileSources.includes('recording'), () => html`
                                <sp-button
                                        id="take-picture-button"
                                        class=${classMap({
                                            'bubble-button-lg': this.type === 'bubble'
                                        })}
                                        ?disabled=${this.disabled || (this.maxlength > 1 && this.value.length >= this.maxlength)}
                                        .icon="url(${getIcon('camera', { size: 'x128'})})"
                                        .text=${this.value.length > 0 && this.maxlength > 1 ? "+1" : nothing}
                                        textToIconPadding="0"
                                        textToBorderPadding="0"
                                        iconAreaWidth=${ buttonCount > 1 ? "3.5rem" : "3.8rem" }
                                        .iconSizePercent=${ this.type === 'bubble' ? 50 : buttonCount > 1 ? 30 : 20 }
                                        @click=${() => {
                                            this.addedFromCamera()
                                        }}>
                                </sp-button>
                            `)
                        }
                        ${
                            when(this.allowedFileSources.includes('localFile'), () => html`
                                <sp-button
                                    id="load-file-button"
                                    class=${ classMap({
                                        'bubble-button-lg': this.type === 'bubble'
                                    })}
                                    ?disabled=${this.disabled || (this.maxlength > 1 && this.value.length >= this.maxlength)}
                                    .icon="url(${getIcon('image', { size: 'x128' })})"
                                    .text=${ this.value.length > 0 && this.maxlength > 1 ? "+1" : nothing }
                                    textToIconPadding="0"
                                    textToBorderPadding="0"
                                    iconAreaWidth=${ buttonCount > 1 ? "3.5rem" : "3.8rem" }
                                    .iconSizePercent=${ this.type === 'bubble' ? 50 : buttonCount > 1 ? 30 : 20 } 
                                    @click=${ this.openFilePicker }>
                                </sp-button>`)
                        }
                    </div>
                    ${
                        when(this.value.length > 1 || (this.value.length === 1 && this.maxlength > 1), () => html`
                            <div class=${ 
                                    classMap({
                                        gallery: true,
                                        bubble: this.type === 'bubble'
                                    })}
                                style=${
                                    styleMap({
                                        gridTemplateColumns: `repeat(${ this.entriesPerRow }, 1fr)`
                                    })    
                                }>
                                ${ this.value.map((value, index) => html`
                                        <sp-image-input-entry
                                            id="image-input-entry-${index}"
                                            style=${styleMap({
                                                ...index === 0 && { display: 'none' }
                                            })}
                                            draggable=${ !!SpImageService.getUrlOrBlobFromImage(value) && !this.disabled && !this.viewMode }
                                            @dragstart=${ e => { this.imageDragStart(e, index) } }
                                            @image-dragenter=${ e => this.imageDragEnter(e,index) }
                                            @open=${ e => { this.openImage(e.detail.image, index,  e.detail.canvas) } }
                                            .image=${ SpImageService.getUrlOrBlobFromImage(value) }
                                            .canvas=${ (value as SpFile).canvas }
                                            .type=${ this.type }
                                            .placeholder=${this.placeholder}
                                            .uuid=${this.imageInputId}>
                                        </sp-image-input-entry>`) }
                                ${ (this.viewMode ? [] : this.getPlaceholderEntries()).map(
                                        (value, index) => html`
                                        <sp-image-input-entry
                                            class="placeholder"
                                            @open=${ e => { this.openFilePicker() } }
                                            .type=${ this.type }
                                            .placeholder=${this.placeholder}>
                                        </sp-image-input-entry>`) }
                            </div>
                        `)
                    }
                    <input hidden id="filePicker" ?disabled=${ this.disabled } type="file" @change="${this.addedFromFilepicker}" accept=${this.allowedFileTypes}>
                </div>
            </div>
            ${ when(this.rootImage, () => html`
                <sp-field-container
                    .label=${ html`${unsafeHTML(this.rootImageLabel)}` }>
                    <sp-image-input-entry 
                        id="image-input-entry-preview"
                        draggable=${ false }
                        .movable=${ false }
                        @open=${ e => { this.openRootImage() } }
                        .image=${ SpImageService.buildUrl(this.rootImage.spec.type, 'images', this.rootImage.path) }
                        .canvas=${ (this.value[0] as SpFile)?.canvas }
                        .placeholder=${ this.placeholder }
                        .type=${ this.type }
                        .uuid=${this.imageInputId}>
                    </sp-image-input-entry>
                </sp-field-container>`) }`;
    }

    buttonCount() {
        let count = 0;
        if (this.cameraDevice && this.allowedFileSources.includes('recording')) count++;
        if (this.allowedFileSources.includes('localFile')) count++;
        return count;
    }

    openFilePicker() {
        this.shadowRoot.getElementById('filePicker').click();
    }

    getPlaceholderEntries() {
        if (this.value.length >= this.maxlength)
            return [];
        const valueLength = this.value.length - 1;
        const missingImages = this.maxlength - valueLength - 1;
        const missingForFullRow = this.entriesPerRow - (valueLength - (Math.floor(valueLength / this.entriesPerRow) * this.entriesPerRow));
        let missingForRow = missingForFullRow;
        if (missingImages < missingForFullRow)
            missingForRow = missingImages;
        return Array(missingForRow).fill({});
    }

    imageDragStart(e: DragEvent, currentIndex: number) {
        this.latestDraggingMovePosition = currentIndex;
        e.dataTransfer.clearData();
        e.dataTransfer.setData(`sp-image-input-entry-move-${this.imageInputId}`, '');
        e.dataTransfer.setDragImage(this.shadowRoot.getElementById(`image-input-entry-${currentIndex}`), 0, 0);
    }

    imageDragEnter(e: DragEvent, toIndex: number) {
        if (toIndex !== this.latestDraggingMovePosition && !this.disabled && !this.viewMode) {
            this.value[toIndex] = this.value.splice(this.latestDraggingMovePosition, 1, this.value[toIndex])[0];
            this.latestDraggingMovePosition = toIndex;
            this.emit(
                'change',
                { detail: { value: this.value } }
            );
        }
    }

    deleteEntry(index: number) {
        this.value.splice(index, 1);
        this.latestDraggingMovePosition = null;
        this.emit(
            'change',
            {
                detail: {
                    value: this.value
                }
            }
        );
    }

    // Restores the original image from canvas (if canvas exists)
    resetOriginalImages() {
        let resets = 0;
        for (const image of this.value) {
            if ('canvas' in image && image.canvas) {
                (image as SpFile).blob = SpImageService.dataURLtoBlob((image.canvas as any).objects[0].src);
                delete image.canvas;
                delete (image as any).path;
                resets++;
            }
        }
        if (resets > 0)
            this.emit(
                'change',
                {
                    detail: {
                        value: this.value
                    }
                }
            );
    }

    async getExif(image: Blob): Promise<SpExif>{
        let exifParsed;
        try {
            exifParsed = await exifr.parse(image, true);
        } catch (e) {
            console.error(`Error parsing exif: ${e}`)
        }
        const exif: SpExif = {}
        if (exifParsed?.longitude && exifParsed.latitude) {
            exif.coords = {
                longitude:  exifParsed.longitude,
                latitude: exifParsed.latitude
            }
        }
        if (exifParsed?.DateTimeOriginal) {
            exif.datetime = exifParsed.DateTimeOriginal;
        }
        return ('coords' in exif || 'datetime' in exif) ? exif : undefined;
    }

    // Closes dialogs or gallery if open
    closeAllOpenDialogs() {
        this.photoswipeController.close();
        this.editorDialog.hide();
        this.errorDialog.hide();
    }
}

export interface SpImageInputChangeEvent extends Event {
    detail: { value: (SpFile | SpRemoteFile)[] };
}
